import React from "react";
import { Link } from "react-router-dom";
import { Check, X, Minus, Lock, ArrowRight, Lock as LockIcon, ShieldCheck, CreditCard, XCircle, Server, Gift } from "lucide-react";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import PublicHeader from "@/components/PublicHeader";
import { useMeta } from "@/lib/useMeta";

const GOLD = "#C9A84C";

const PLANS = [
  {
    id: "trial", name: "Free Trial", price: "₹0", unit: "for 14 days", badge: "Start Here",
    included: ["1 project package", "WBS and BOQ tracking", "DPR management", "Site photo upload", "Up to 5 team members", "WhatsApp DPR alerts", "Email support"],
    locked: ["AI Risk Radar", "BOQ Parser AI", "Predictive completion forecast", "Multi-project dashboard", "Priority support"],
    cta: ["Start Free Trial", "/signup"], solid: true,
  },
  {
    id: "professional", name: "Professional", price: "₹4,999", unit: "per month, per project", badge: "Most Popular", featured: true,
    included: ["Everything in Free Trial", "AI Risk Radar", "BOQ Parser AI", "Predictive completion forecast", "Up to 10 team members", "WhatsApp and email alerts", "Full audit trail", "Priority support"],
    locked: [],
    cta: ["Get Started", "/signup"], solid: true,
  },
  {
    id: "enterprise", name: "Enterprise", price: "Custom", unit: "talk to us",
    included: ["Everything in Professional", "Unlimited projects", "Unlimited team members", "Dedicated onboarding", "Custom WBS templates", "API access", "SLA-backed uptime", "Dedicated account manager"],
    locked: [],
    cta: ["Contact Us", "/contact"], solid: false,
  },
];

const TABLE = [
  ["Project Management", [
    ["Projects included", "1", "1 per licence", "Unlimited"],
    ["WBS with real quantities", true, true, true],
    ["BOQ tracking and rates", true, true, true],
    ["Critical path and float", true, true, true],
    ["Earned value (CPI / SPI / EAC)", "partial", true, true],
    ["What-if scenario modelling", false, true, true],
    ["Custom WBS templates", false, false, true],
  ]],
  ["AI and Analytics", [
    ["AI Risk Radar", false, true, true],
    ["AI BOQ Parser", false, true, true],
    ["Predictive completion forecast", false, true, true],
    ["AI management brief", false, true, true],
    ["Multi-project portfolio dashboard", false, "partial", true],
  ]],
  ["Reporting and DPR", [
    ["Daily Progress Reports", true, true, true],
    ["Offline DPR with auto-sync", true, true, true],
    ["Site photo evidence", true, true, true],
    ["Excel, PPTX and PDF exports", "partial", true, true],
    ["Power BI / OData live feed", false, true, true],
  ]],
  ["Team and Access", [
    ["Team members", "5", "10", "Unlimited"],
    ["Role-based permissions", true, true, true],
    ["Company data isolation", true, true, true],
    ["Admin audit trail", false, true, true],
    ["API access", false, false, true],
  ]],
  ["Support and SLA", [
    ["Email support", true, true, true],
    ["Priority support", false, true, true],
    ["Dedicated onboarding", false, false, true],
    ["Account manager", false, false, true],
    ["SLA-backed uptime", false, false, true],
  ]],
];

const TRUST = [
  [Gift, "14-Day Free Trial, No Credit Card Required"],
  [CreditCard, "Razorpay Secured Payments"],
  [XCircle, "Cancel Anytime"],
  [ShieldCheck, "DPDP Act 2023 Compliant"],
  [Server, "Indian Data Hosting"],
];

const FAQS = [
  ["Can I upgrade mid-trial?", "Yes. Upgrade any day during the 14 days and everything you have already built — projects, WBS, BOQ, DPRs — carries straight over. The AI modules unlock the moment the payment is verified."],
  ["What happens to my data if I cancel?", "It stays available for 30 days so you can reactivate or export it in full (Excel, PDF, PPTX), then it is deleted from live systems. Invoices are retained for eight years because Indian tax law requires it."],
  ["Is GST included in the price?", "No. ₹4,999 per month per project is exclusive of GST, which is added at checkout at the applicable rate and shown on your invoice."],
  ["Can I add more team members?", "Professional includes 10 named users per project licence. Beyond that, additional seats or an Enterprise plan with unlimited users can be arranged — write to us and we will quote it."],
  ["Do you offer annual discounts?", "Yes. Annual pre-payment carries a discount, and multi-project portfolios are priced better than per-project licences. Contact us with your project count for a quote."],
  ["How do I get enterprise pricing?", "Send us your project count, team size and the modules you need. We usually respond within one business day with a written quote, and we can run a paid pilot on one live package first."],
];

const Mark = ({ v }) =>
  v === true ? <Check className="w-4 h-4 mx-auto" style={{ color: GOLD }} /> :
  v === false ? <X className="w-4 h-4 mx-auto text-white/25" /> :
  v === "partial" ? <Minus className="w-4 h-4 mx-auto text-white/45" /> :
  <span className="num text-[12px]">{v}</span>;

export default function Pricing() {
  useMeta({
    title: "EPC Project Management Software Pricing India — Plan-Sarthi",
    description:
      "Plan-Sarthi pricing: free 14-day trial with no credit card, Professional at ₹4,999 per month per project with AI Risk Radar and BOQ Parser, and custom Enterprise plans. GST extra, Razorpay secured.",
    path: "/pricing",
  });

  return (
    <div className="min-h-screen bg-[#0A1628] text-white" data-testid="pricing-page">
      <PublicHeader />

      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-14">
        <div className="text-center">
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight" data-testid="pricing-headline">
            Simple, Transparent Pricing for <span style={{ color: GOLD }}>EPC Teams of Every Size</span>
          </h1>
          <p className="text-base text-white/70 mt-5 max-w-[640px] mx-auto leading-relaxed">
            Start free for 14 days. No credit card required. Upgrade when you are ready.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-5 mt-12 items-start">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`bg-[#112240] rounded-lg p-6 relative ${plan.featured ? "lg:-mt-4 lg:pb-10 border-2" : "border"}`}
              style={{ borderColor: plan.featured ? GOLD : "rgba(255,255,255,0.1)" }}
              data-testid={`plan-${plan.id}`}
            >
              {plan.badge ? (
                <span className="absolute -top-3 left-6 text-[10.5px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded"
                      style={{ backgroundColor: GOLD, color: "#0A1628" }} data-testid={`plan-badge-${plan.id}`}>
                  {plan.badge}
                </span>
              ) : null}
              <h2 className="font-semibold text-lg mt-2">{plan.name}</h2>
              <div className="mt-4">
                <span className="font-display text-3xl font-bold num" style={{ color: GOLD }}>{plan.price}</span>
                <span className="text-[12.5px] text-white/55 ml-2">{plan.unit}</span>
              </div>
              <Link
                to={plan.cta[1]}
                className={`mt-6 flex items-center justify-center h-11 rounded-lg text-[14.5px] font-semibold transition-colors duration-200 ${plan.solid ? "text-white" : "border"}`}
                style={plan.solid ? { backgroundColor: GOLD } : { borderColor: GOLD, color: GOLD }}
                data-testid={`plan-cta-${plan.id}`}
              >
                {plan.cta[0]} <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <ul className="mt-6 space-y-2.5">
                {plan.included.map((f) => (
                  <li key={f} className="flex gap-2.5 text-[13px]">
                    <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: GOLD }} /> {f}
                  </li>
                ))}
                {plan.locked.map((f) => (
                  <li key={f} className="flex gap-2.5 text-[13px] text-white/35" data-testid={`plan-locked-${plan.id}`}>
                    <Lock className="w-4 h-4 shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <section className="mt-16" data-testid="comparison-table">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-center">Compare every feature</h2>
          <div className="mt-8 overflow-x-auto">
            <table className="w-full text-[13px] min-w-[640px]">
              <thead className="sticky top-16">
                <tr className="border-b border-white/15 bg-[#0A1628]">
                  <th className="text-left font-medium py-3 px-3 w-[42%]">Feature</th>
                  <th className="py-3 px-3 text-center font-medium">Free Trial</th>
                  <th className="py-3 px-3 text-center font-semibold" style={{ color: GOLD }}>Professional</th>
                  <th className="py-3 px-3 text-center font-medium">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {TABLE.map(([group, rows]) => (
                  <React.Fragment key={group}>
                    <tr data-testid={`table-group-${group.replace(/\s/g, "-").toLowerCase()}`}>
                      <td colSpan={4} className="pt-6 pb-2 px-3 text-[11px] uppercase tracking-[0.16em]" style={{ color: GOLD }}>{group}</td>
                    </tr>
                    {rows.map(([label, a, b, c]) => (
                      <tr key={label} className="border-b border-white/5">
                        <td className="py-2.5 px-3 text-white/80">{label}</td>
                        <td className="py-2.5 px-3 text-center"><Mark v={a} /></td>
                        <td className="py-2.5 px-3 text-center bg-white/[0.03]"><Mark v={b} /></td>
                        <td className="py-2.5 px-3 text-center"><Mark v={c} /></td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11.5px] text-white/40 mt-4">
            <Check className="w-3 h-3 inline" style={{ color: GOLD }} /> included ·{" "}
            <Minus className="w-3 h-3 inline text-white/45" /> partial ·{" "}
            <X className="w-3 h-3 inline text-white/25" /> not included
          </p>
        </section>

        <section className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 border-y border-white/10 py-6" data-testid="pricing-trust-strip">
          {TRUST.map(([Icon, text]) => (
            <div key={text} className="flex items-start gap-2">
              <Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color: GOLD }} />
              <span className="text-[11.5px] text-white/70 leading-snug">{text}</span>
            </div>
          ))}
        </section>

        <section className="mt-14" data-testid="pricing-faq">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-center">Pricing questions</h2>
          <div className="grid md:grid-cols-2 gap-4 mt-8">
            {FAQS.map(([q, a], i) => (
              <div key={q} className="bg-[#112240] border border-white/10 rounded-lg p-5" data-testid={`pricing-faq-${i}`}>
                <h3 className="font-semibold text-[14px]">{q}</h3>
                <p className="text-[12.5px] text-white/60 mt-2.5 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14 border rounded-lg p-7 sm:p-9 text-center" style={{ borderColor: "rgba(201,168,76,0.35)", background: "rgba(201,168,76,0.07)" }} data-testid="pricing-bottom-cta">
          <h2 className="font-display text-2xl sm:text-3xl font-bold">Still not sure? Talk to our team.</h2>
          <p className="text-[13.5px] text-white/65 mt-3 max-w-[560px] mx-auto leading-relaxed">
            Tell us your project count and team size, and we will tell you honestly which plan fits — or that you
            should stay on the trial a little longer.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-7">
            <Link to="/contact" className="inline-flex items-center h-11 px-6 rounded-lg text-[15px] font-semibold text-white" style={{ backgroundColor: GOLD }} data-testid="pricing-contact-btn">
              Contact Us <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <Link to="/demo/solar-epc" className="inline-flex items-center h-11 px-6 rounded-lg border border-white/35 text-[15px] text-white hover:bg-white/10 transition-colors duration-200" data-testid="pricing-demo-btn">
              Explore a live demo
            </Link>
          </div>
        </section>
      </div>

      <Footer />
      <BackToTop />
    </div>
  );
}
